import React, { useState, useEffect } from 'react';
import { Calendar, Tooltip } from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { Col, Container, Row } from 'react-bootstrap';
import './DateSelection.css';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useBookingData } from '../../../context/BookingDataContext';
import { useData } from '../../../context/DataContext';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const DateSelection = ({ onDateSelect }) => {
  const { setBookingData, bookingData} = useBookingData();
  const { settings } = useData();
  const [currentMonth, setCurrentMonth] = useState(dayjs().tz("Asia/Kolkata"));
 
  const getISTTime = () => dayjs().tz("Asia/Kolkata");

  const handleClick = (date, targetMonth) => {
    const now = getISTTime();
    const startingDate = dayjs("2025-05-11").tz("Asia/Kolkata").startOf("day");
    const cutoffStart = startingDate.add(15, "hour");

    if (
      date.isSame(startingDate, "date") &&
      now.isAfter(cutoffStart)
    ) {
      return;
    }

    if (!date.isBefore(startingDate, "date") && date.isSame(targetMonth, "month")) {
      setBookingData((prevData) => ({
        ...prevData,
        bookingDate: date.format("DD/MM/YY"),
      }));
      onDateSelect(date.format("DD/MM/YY"));
    }
  };

  const goToPreviousMonth = () => {
    if (!currentMonth.isSame(dayjs(), 'month')) {
      setCurrentMonth(currentMonth.subtract(1, 'month'));
    }
  };

  const goToNextMonth = () => {
    setCurrentMonth(currentMonth.add(1, 'month'));
  };

  const cellRender = (date, targetMonth) => {
    const now = getISTTime();
    const startingDate = dayjs("2025-05-13").tz("Asia/Kolkata").startOf("day");
    const cutoffTime = startingDate.clone().add(15, "hour");

    const isSelected =
      bookingData?.bookingDate &&
      date.isSame(dayjs(bookingData.bookingDate, "DD/MM/YY"), "date");

    const isPastDate = date.isBefore(now.startOf("day"), "date");  // ✅ fixed here
    const isSameMonth = date.isSame(targetMonth, "month");

    // Convert settings dates and calendar date to dayjs at start of day
    const settingsStartDate = dayjs(settings?.startDate).tz("Asia/Kolkata").startOf("day");
    const settingsEndDate = dayjs(settings?.endDate).tz("Asia/Kolkata").startOf("day");
    const calendarDate = date.clone().tz("Asia/Kolkata").startOf("day");

    // Only disable date range if activeStatus is FALSE
    const isInDisabledRange =
      settings?.activeStatus === false &&
      calendarDate.isSameOrAfter(settingsStartDate, "day") &&
      calendarDate.isSameOrBefore(settingsEndDate, "day");

    // To disable weekday in june and july 2025
    const month = calendarDate.month(); // 0 = Jan, 5 = June, 6 = July
    const year = calendarDate.year();
    const dayOfWeek = calendarDate.day(); // 0 = Sunday, 6 = Saturday

    const isWeekdayInJuneOrJuly2025 = (month === 5 || month === 6) && year === 2025 && dayOfWeek >= 1 && dayOfWeek <= 5;

    const isDisabled =
      isWeekdayInJuneOrJuly2025 ||
      (date.isSame(startingDate, "date") && now.isAfter(cutoffTime)) ||
      isPastDate ||
      isInDisabledRange;

    if (!isSameMonth) return <div></div>;

    return (
      <Tooltip
        title={
          isInDisabledRange ? "Booking not available on this date" : null
        }
        placement="top"
        mouseEnterDelay={0.3}
      >
        <div
          className="date-select mx-3 my-2 d-flex justify-content-center align-items-center"
          style={{
            width: '50px',
            height: '50px',
            backgroundColor: isSelected ? '#1890ff' : 'transparent',
            color: isDisabled ? '#d9d9d9' : '#000',
            borderRadius: '50px',
            textAlign: 'center',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
          }}
          onClick={() => !isDisabled && handleClick(date, targetMonth)}
        >
          <p
            className={`${
              window?.innerWidth > 1025.98
                ? 'fs-20'
                : window?.innerWidth <= 426
                ? 'fs-18'
                : 'fs-20'
            } fw-700 m-0`}
          >
            {date.date()}
          </p>
        </div>
      </Tooltip>
    );
  };  
  
  return (
    <Container className='my-5' >
      <div className='pb-3 text-mob'>
        <h4 className='fs-28 fw-700 text-black'>
          Select your visit date
        </h4>
      </div>

      <Row 
        className='d-flex align-items-center justify-content-center' 
        style={{ borderRadius: '24px', backgroundColor: '#FFFF', border: '1px solid #A0A1AB' }}
      >
        <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12} className='px-0'>    
          <Row className='m-2 calendar-date' style={{backgroundColor: '#ffff', borderRadius: '16px'}}>
            <Col xxl={12} xl={12} lg={12} md={12} sm={12} xs={12}>
              <Calendar
                className="custom-calendar"
                value={currentMonth}
                fullscreen={false}
                headerRender={({ value }) => (
                  <Row className='p-4 mob-calendar'>
                    <Col xxl={6} xl={6} lg={6} md={6} sm={8} xs={8} className='d-flex align-items-center justify-content-start'>
                      <p
                        className={`${
                          window?.innerWidth > 1025.98
                            ? "fs-18"
                            : window?.innerWidth <= 426
                            ? "fs-16"
                            : "fs-18"
                        } fw-700 text-black calendar-month text-center mb-0`}
                      >
                        {value.format('MMMM YYYY')}
                      </p>
                    </Col>
                    <Col xxl={6} xl={6} lg={6} md={6} sm={4} xs={4} className='d-flex align-items-center justify-content-end'>
                      <div style={{ display: "flex", gap: "10px"}}>
                        <span
                          style={{
                            width: "30px",
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            background: "#fff",
                            border: '2px solid #272727',
                            cursor: currentMonth.isSame(dayjs(), "month")
                              ? "not-allowed"
                              : "pointer",
                          }}
                          onClick={goToPreviousMonth}
                        >
                          <LeftOutlined
                            style={{
                              fontSize: "12px",
                              color: currentMonth.isSame(dayjs(), "month") ? "#d9d9d9" : "#000",
                            }}
                          />
                        </span>
                        <span
                          style={{
                            width: "30px",
                            height: "30px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "50%",
                            background: "#fff",
                            border: '2px solid #272727',
                            cursor: "pointer",
                          }}
                          onClick={goToNextMonth}
                        >
                          <RightOutlined style={{ fontSize: "12px" }} />
                        </span>
                      </div>
                    </Col>
                  </Row>
                )}
                dateFullCellRender={(date) => cellRender(date, currentMonth)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </Container>
  );
};

export default DateSelection;
